import {mxResources} from '../mxGraphFactory';
import isEmpty from 'lodash/isEmpty';
import Queue from './Queue';
import {createLogger} from '../../Common/logger';

const LOGGER = createLogger("GraphValidator");

export default class GraphValidator {
    constructor(name, startTask, cell, model) {
        this.model = model;
        this.name = name;
        this.steps = [];
        this.routes = [];
        this.startTask = startTask;

        this.seen = [];
        this.startTaskIdex = -1;
        this.visitOrdinal = 0;
        this.beenThere = new Set();

        this.convertCells(cell)
    }

    convertCells(cell) {
        if (cell.isVertex()) {
          this.steps.push(cell);
        } else if (cell.isEdge()) {
          this.routes.push(cell);
        }

        const childCount = cell.getChildCount();
        for (let i = 0; i < childCount; i++) {
            this.convertCells(cell.getChildAt(i));
        }
    }

    startTaskIndex() {
        return this.steps.findIndex(step => step.id === this.startTask);
    }

    validate(validationContext) {
        const actualStepCount = this.steps.length;
        const startTaskError = {
            message: `The graph ${this.name} contains no tasks. Graphs should always contain at least a task`,
            graphName: this.name
        };

        if (actualStepCount <= 0) {
            validationContext.addError(startTaskError);
        } else {
            this.seen = [];
            if (this.canReachAllSteps(actualStepCount)) {
                this.validateUniqueStepNames(validationContext);

                if (this.startTaskIndex() === -1) {
                    validationContext.addError(startTaskError);
                } else {
                    this.validateAndFixupSplit(actualStepCount, this.startTaskIndex(), validationContext);
                }
            } else {
                for (let i = 0; i < actualStepCount; ++i) {
                    if (this.seen[i] === -1) {
                        validationContext.addStepError({
                            message: "Task cannot be reached from the start/launch task",
                            graphName: this.name,
                            stepId: this.steps[i].id,
                            stepName: this.steps[i].value.name
                        });
                    }
                }
            }
        }
    }

    canReachAllSteps(numberOfSteps) {
        let reachAllSteps = true;

        for (let i = 0; i < numberOfSteps; ++i) {
            this.seen[i] = -1;
        }

        this.visitOrdinal = 0;

        this.seen[0] = ++this.visitOrdinal;

        this.visit(this.startTaskIndex());

        for (let i = 0; i < numberOfSteps; ++i) {
            if (this.seen[i] === -1) {
                reachAllSteps = false;
                break;
            }
        }

        return reachAllSteps;
    }

    validateUniqueStepNames(validationContext) {
        const stepNames = new Set();

        this.steps.forEach(step => {
            const {id, value: {name}} = step;

            if (!name || isEmpty(name)) {
                validationContext.addStepError({
                    fields: {
                        name: mxResources.get('taskNameMustNotBlank')
                    },
                    graphName: this.name,
                    stepId: id,
                    stepName: name
                });
            } else if (stepNames.has(name)) {
                validationContext.addStepError({
                    fields: {
                        name: mxResources.get('taskNameMustBeUnique')
                    },
                    graphName: this.name,
                    stepId: id,
                    stepName: name
                });
            } else {
                stepNames.add(name)
            }
        });
    }

    validateAndFixupSplit(maxSteps, theStartStepIndex, validationContext) {
        LOGGER.debug("ENTER", "validateAndFixupSplit");
        LOGGER.debug("validateAndFixupSplit", `>>> GRAPH NAME IS <${this.name}>`);

        this.visitOrdinal = 0;
        this.seen = [];

        const bfsQueue = new Queue(maxSteps);
        const previous = [];
        const colorStrings = [];

        for (let i = 0; i < maxSteps; ++i) {
            this.seen[i] = -1;
            colorStrings[i] = "";
            previous[i] = 0;
        }

        bfsQueue.put(theStartStepIndex);

        LOGGER.debug("validateAndFixupSplit", ">>> VISIT AND COLOR all steps.");

        while (true) {
            let currentStepIndex;
            let outRoutes;
            do {
                if (bfsQueue.isEmpty()) {
                    for (let i = 0; i < maxSteps; ++i) {
                        this.steps[i].value.joinTask = null;
                    }

                    let gotError = false;

                    LOGGER.debug("validateAndFixupSplit", ">>> RE-VISITING STEPS to check nesting and fixup joinTask.");

                    this.visitOrdinal = 0;
                    if (this.seen == null || this.seen.length !== maxSteps) {
                        this.seen = [];
                    }

                    for (let i = 0; i < maxSteps; ++i) {
                        this.seen[i] = -1;
                    }

                    bfsQueue.put(theStartStepIndex);

                    while (!bfsQueue.isEmpty()) {
                        currentStepIndex = bfsQueue.get();
                        this.seen[currentStepIndex] = ++this.visitOrdinal;

                        LOGGER.debug("validateAndFixupSplit", `   Re-Visiting Step id ${this.steps[currentStepIndex].id} [${this.steps[currentStepIndex].value.name}], color is <${colorStrings[this.getIndexOfStepId(this.steps[currentStepIndex].id)]}>`);

                        let incomingRoutes;
                        if (['And', 'Or'].includes(this.steps[currentStepIndex].value.joinType)) {
                            incomingRoutes = this.getRoutesByDestinationStep(this.steps[currentStepIndex].id);
                            if (incomingRoutes.length > 0) {
                                for (let i = 0; i < incomingRoutes.length; ++i) {
                                    if (colorStrings[this.getIndexOfStepId(incomingRoutes[0].source.id)] !== colorStrings[this.getIndexOfStepId(incomingRoutes[i].source.id)]) {
                                        gotError = true;
                                        validationContext.addRouteError({
                                            message: mxResources.get('taskIncorrectlyNested', [incomingRoutes[i].source.value.name, incomingRoutes[i].target.value.name]),
                                            graphName: this.name,
                                            routeId: incomingRoutes[i].id,
                                            routeName: incomingRoutes[i].value.name
                                        });
                                    }
                                }

                                if (!gotError && this.steps[currentStepIndex].value.joinType === 'And' && colorStrings[this.getIndexOfStepId(incomingRoutes[0].source.id)] !== '') {
                                    let tempColor = colorStrings[this.getIndexOfStepId(incomingRoutes[0].source.id)];
                                    const andSplitStep = this.getStep(tempColor.substring(tempColor.lastIndexOf('.') + 1));
                                    if (andSplitStep.value.joinTask) {
                                        gotError = true;
                                        validationContext.addStepError({
                                            message: `Ambiguous split/join pairing, split step ${andSplitStep.value.name} can match join step ${this.getStep(andSplitStep.value.joinTask).value.name} or ${this.steps[currentStepIndex].value.name}`,
                                            graphName: this.name,
                                            stepId: andSplitStep.id,
                                            stepName: andSplitStep.value.name
                                        });
                                    } else {
                                        andSplitStep.value.joinTask = this.steps[currentStepIndex].id;
                                        LOGGER.debug("validateAndFixupSplit", `   SPLIT STEP FIXUP: AND-SPLIT [${andSplitStep.value.name}] paired with AND-JOIN [${this.steps[currentStepIndex].value.name}]`);
                                    }
                                }
                            }
                        }

                        if ((incomingRoutes = this.model.getEdges(this.steps[currentStepIndex], false, true, true)).length > 0) {
                            for (let i = 0; i < incomingRoutes.length; ++i) {
                                const j = this.getIndexOfStepId(incomingRoutes[i].target.id);
                                if (this.seen[j] === -1) {
                                    bfsQueue.put(j);
                                    this.seen[j] = -2;
                                }
                            }
                        }
                    }

                    if (!gotError) {
                        LOGGER.debug("validateAndFixupSplit", ">>> CHECKING that all AND-SPLITS have been fixed up.");

                        for (let i = 0; i < maxSteps; ++i) {
                            if (this.steps[i].value.splitType === 'And' && !this.steps[i].value.joinTask) {
                                gotError = true;
                                validationContext.addStepError({
                                    fields: {
                                        takeRoutesOf: mxResources.get('taskNoCollectorTask', [this.steps[i].value.name])
                                    },
                                    graphName: this.name,
                                    stepId: this.steps[i].id,
                                    stepName: this.steps[i].value.name
                                });
                            }
                        }
                    }

                    currentStepIndex = this.routes.length;

                    for (let i = 0; i < currentStepIndex; ++i) {
                        this.routes[i].released = false;
                    }

                    if (!gotError) {
                        LOGGER.debug("validateAndFixupSplit", ">>> Begin Route RELEASE processing");

                        let bfsVisitOrdinal = 0;
                        const bfsSeen = [];

                        for (let i = 0; i < maxSteps; ++i) {
                            bfsSeen[i] = -1;
                        }

                        bfsQueue.put(theStartStepIndex);

                        endRouteRelease:
                            while (true) {
                                let outGoingRoutes;
                                do {
                                    if (bfsQueue.isEmpty()) {
                                        LOGGER.debug("validateAndFixupSplit", ">>> End Route RELEASE processing");
                                        break endRouteRelease;
                                    }

                                    currentStepIndex = bfsQueue.get();
                                    ++bfsVisitOrdinal;
                                    bfsSeen[currentStepIndex] = bfsVisitOrdinal;
                                    if (this.steps[currentStepIndex].value.splitType === 'And' || this.steps[currentStepIndex].value.splitType === 'Or') {
                                        let targetJoinTask;
                                        if (this.steps[currentStepIndex].value.splitType === 'And') {
                                            targetJoinTask = this.steps[currentStepIndex].value.joinTask;
                                        } else if (colorStrings[this.getIndexOfStepId(this.steps[currentStepIndex].id)] !== '') {
                                            const tempColor = colorStrings[this.getIndexOfStepId(this.steps[currentStepIndex].id)];
                                            const stepId = tempColor.substring(tempColor.lastIndexOf('.') + 1);
                                            targetJoinTask = this.getStep(stepId).value.joinTask;
                                        }

                                        outGoingRoutes = this.getRoutesBySourceStep(this.steps[currentStepIndex].id);
                                        if (!targetJoinTask && outGoingRoutes != null) {
                                            if (this.seen == null || this.seen.length !== maxSteps) {
                                                this.seen = [];
                                            }

                                            for (let j = 0; j < outGoingRoutes.length; ++j) {
                                                for (let k = 0; k < maxSteps; ++k) {
                                                    this.seen[k] = -1;
                                                }

                                                this.visitOrdinal = 0;
                                                this.visit(this.getIndexOfStepId(outGoingRoutes[j].target.id));
                                                if (this.seen[this.getIndexOfStepId(targetJoinTask)] === -1) {
                                                    const tempColor = colorStrings[this.getIndexOfStepId(this.steps[currentStepIndex].id)];
                                                    const parentAndSplitId = tempColor.substring(tempColor.lastIndexOf('.') + 1);
                                                    this.beenThere.clear();
                                                    if (!this.alreadyReleased(parentAndSplitId, this.steps[currentStepIndex].id)) {
                                                        LOGGER.debug("validateAndFixupSplit", "   RELEASE: releasing route from [" + this.getStep(outGoingRoutes[j].source.id).value.name + "] TO [" + this.getStep(outGoingRoutes[j].target.id).value.name + "]");

                                                        outGoingRoutes[j].released = true;
                                                    }
                                                }
                                            }
                                        }
                                    }
                                } while ((outGoingRoutes = this.model.getEdges(this.steps[currentStepIndex], false, true, true)).length === 0);

                                for (let i = 0; i < outGoingRoutes.length; ++i) {
                                    let j = this.getIndexOfStepId(outGoingRoutes[i].target.id);
                                    if (bfsSeen[j] === -1) {
                                        bfsQueue.put(j);
                                        bfsSeen[j] = -2;
                                    }
                                }
                            }
                    }

                    LOGGER.debug("EXIT", "validateAndFixupSplit");
                    return;
                }

                currentStepIndex = bfsQueue.get();
                this.seen[currentStepIndex] = ++this.visitOrdinal;
                if (this.steps[currentStepIndex].value.splitType !== 'And' && this.steps[currentStepIndex].value.joinType !== 'And') {
                    colorStrings[currentStepIndex] = colorStrings[previous[currentStepIndex]];
                } else {
                    if (this.steps[currentStepIndex].value.joinType === 'And' && colorStrings[previous[currentStepIndex]] !== '') {
                        let newColor = colorStrings[previous[currentStepIndex]];
                        const targetJoinStepId = newColor.lastIndexOf('.');
                        newColor = newColor.substring(0, targetJoinStepId);
                        colorStrings[currentStepIndex] = newColor;
                    }

                    if (this.steps[currentStepIndex].value.splitType === 'And') {
                        if (this.steps[currentStepIndex].value.joinType === 'And') {
                            colorStrings[currentStepIndex] = colorStrings[currentStepIndex] + '.' + this.steps[currentStepIndex].id;
                        } else {
                            colorStrings[currentStepIndex] = colorStrings[previous[currentStepIndex]] + '.' + this.steps[currentStepIndex].id;
                        }
                    }
                }

                LOGGER.debug("validateAndFixupSplit", `   After Visiting [${this.steps[currentStepIndex].value.name}], color is  <${colorStrings[currentStepIndex]}>`);
            } while ((outRoutes = this.model.getEdges(this.steps[currentStepIndex], false, true, true)).length === 0);

            for (let i = 0; i < outRoutes.length; ++i) {
                const targetJoinTaskIndex = this.getIndexOfStepId(outRoutes[i].target.id);
                if (this.seen[targetJoinTaskIndex] === -1) {
                    bfsQueue.put(targetJoinTaskIndex);
                    this.seen[targetJoinTaskIndex] = -2;
                    previous[targetJoinTaskIndex] = currentStepIndex;
                }
            }
        }
    }

    visit(theStepIndex) {
        this.seen[theStepIndex] = ++this.visitOrdinal;

        this.model.getEdges(this.steps[theStepIndex], false, true, true).forEach(route => {
            const adjacentStepIndex = this.getIndexOfStepId(route.target.id);
            if (this.seen[adjacentStepIndex] === -1) {
                this.visit(adjacentStepIndex);
            }
        })
    }

    getIndexOfStepId(stepId) {
        return this.steps.findIndex(step => step.id === stepId);
    }

    getRoutesByDestinationStep(theStepId) {
        const routes = [];

        for (let i = 0; i < this.routes.length; ++i) {
            if (this.routes[i].target.id === theStepId) {
                routes.push(this.routes[i]);
            }
        }

        return routes;
    }

    getRoutesBySourceStep(theStepId) {
        const routes = [];

        for (let i = 0; i < this.routes.length; ++i) {
            if (this.routes[i].source.id === theStepId) {
                routes.push(this.routes[i]);
            }
        }

        return routes;
    }

    getStep(theStepId) {
        return this.steps[this.getIndexOfStepId(theStepId)];
    }

    alreadyReleased(splitParentId, startId) {
        LOGGER.debug("ENTER", "alreadyReleased", {splitParentId, startId});

        if (splitParentId !== startId && startId !== 0) {
            let incomingRoutes = this.getRoutesByDestinationStep(startId);

            for (let i = 0; i < incomingRoutes.length; ++i) {
                let prevRoute = incomingRoutes[i];
                if (prevRoute.released) {
                    this.beenThere.clear();
                    LOGGER.debug("EXIT", "alreadyReleased", true);
                    return true;
                }

                if (!this.beenThere.has(prevRoute.source.id)) {
                    this.beenThere.add(prevRoute.source.id);
                    if (this.alreadyReleased(splitParentId, prevRoute.source.id)) {
                        this.beenThere.clear();
                        LOGGER.debug("EXIT", "alreadyReleased", true);
                        return true;
                    }
                }
            }

            return false;
        } else {
            if (splitParentId === startId) {
                LOGGER.debug("alreadyReleased", "startId == splitParentId");
            } else if (startId === 0) {
                LOGGER.debug("alreadyReleased", "startId == 0");
            }

            LOGGER.debug("EXIT", "alreadyReleased", false);
            return false;
        }
    }
}